<template>
  <div class="topic-clustering-tree">
    <CCard>
      <CCardHeader>
        <CRow>
          <CCol md="8">
            <strong>🌳 درختواره تاپیک کلاسترینگ</strong>
            <small class="d-block text-muted">
              نمایش همه صفحات و زیرمجموعه‌های آنها (بر اساس کمترین تعداد کلمه کلیدی)
            </small>
          </CCol>
          <CCol md="4" class="text-left">
            <CBadge color="primary" class="mr-1">
              {{ totalPages }} صفحه
            </CBadge>
            <CBadge color="success" class="mr-1">
              {{ pagesWithChildren }} دارای زیرمجموعه
            </CBadge>
            <CBadge color="danger" class="mr-1">
              {{ orphanPages }} یتیم
            </CBadge>
          </CCol>
        </CRow>
      </CCardHeader>

      <CCardBody>
        <!-- فیلتر جستجو -->
        <CRow class="mb-3">
          <CCol md="6">
            <CInput
                label="جستجو"
                placeholder="نام صفحه را وارد کنید..."
                v-model="searchQuery"
            >
              <template #prepend>
                <CIcon name="cil-magnifying-glass" />
              </template>
            </CInput>
          </CCol>
          <CCol md="6" class="d-flex align-items-end">
            <CButton
                color="primary"
                @click="expandAll"
                class="ml-2"
            >
              📂 باز کردن همه
            </CButton>
            <CButton
                color="secondary"
                @click="collapseAll"
            >
              📁 بستن همه
            </CButton>
          </CCol>
        </CRow>

        <!-- آمار کلی -->
        <CRow class="mb-3">
          <CCol md="4">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-primary">{{ totalPages }}</div>
                <div>تعداد کل صفحات</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="4">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-success">{{ pagesWithChildren }}</div>
                <div>دارای زیرمجموعه ✅</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="4">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-success">{{ Childrens }}</div>
                <div>زیرمجموعه ها ✅</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="4">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-danger">{{ orphanPages }}</div>
                <div>یتیم 🚨</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- درختواره -->
        <div v-if="filteredTree.length === 0" class="text-center py-5">
          <CIcon name="cil-info" size="xl" />
          <p class="mt-2">هیچ داده‌ای برای نمایش وجود ندارد</p>
          <p class="text-muted">لطفاً مقالات را بارگذاری کنید</p>
        </div>

        <div v-else class="tree-container">
          <div
              v-for="(node, index) in filteredTree"
              :key="index"
              class="tree-node"
          >
            <!-- گره اصلی -->
            <div
                class="tree-node-item"
                :class="{
                'tree-node-pillar': node.children && node.children.length > 0 && !node.isOrphan,
                'tree-node-orphan': node.isOrphan
              }"
                @click="toggleNode(node)"
            >
              <span v-if="node.children && node.children.length > 0" class="tree-toggle mr-1" @click.stop="toggleNode(node)">
                <CIcon :name="node.isExpanded ? 'cil-chevron-down' : 'cil-chevron-right'" />
              </span>
              <span v-else class="tree-toggle-placeholder mr-1"></span>

              <CIcon
                  :name="node.isOrphan ? 'cil-warning' : (node.children && node.children.length > 0 ? 'cil-star' : 'cil-file')"
                  :class="node.isOrphan ? 'text-danger' : (node.children && node.children.length > 0 ? 'text-primary' : 'text-secondary')"
                  class="mr-1"
              />

              <span class="tree-title font-weight-bold mr-1">
                {{ node.title }}
              </span>

              <CBadge :color="node.isOrphan ? 'danger' : (node.children && node.children.length > 0 ? 'primary' : 'secondary')" class="mr-1">
                {{ node.keywordCount }} کلمه
              </CBadge>

              <CBadge v-if="node.children && node.children.length > 0" color="success" class="mr-1">
                {{ node.children.length }} زیرمجموعه
              </CBadge>

              <CBadge v-if="node.isOrphan" color="danger" class="mr-1">
                🚨 یتیم
              </CBadge>

              <small class="text-muted mr-1">
                🔗 {{ node.incomingLinks || 0 }} لینک ورودی
              </small>

              <small class="text-muted mr-1">
                📤 {{ node.outgoingLinks || 0 }} لینک خروجی
              </small>
            </div>

            <!-- زیرمجموعه‌ها -->
            <div v-if="node.isExpanded && node.children && node.children.length > 0" class="tree-children">
              <div
                  v-for="(child, childIndex) in node.children"
                  :key="childIndex"
                  class="tree-node-item tree-node-cluster"
                  :style="{ paddingRight: '40px' }"
              >
                <span class="tree-toggle-placeholder mr-1"></span>
                <CIcon name="cil-folder-open" class="text-info mr-1" />
                <span class="tree-title mr-1">
                  {{ child.title }}
                </span>
                <CBadge color="secondary" class="mr-1">
                  {{ child.keywordCount }} کلمه
                </CBadge>
                <small class="text-muted mr-1">
                  🔗 لینک به: {{ node.title }}
                </small>
                <small class="text-muted mr-1">
                  با لنگر: "{{ child.anchorText || 'نامشخص' }}"
                </small>
                <CBadge color="info" class="mr-1">
                  {{ child.incomingLinks || 0 }} ورودی
                </CBadge>
              </div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
export default {
  name: 'TopicClusteringTree',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    stopWords: {
      type: Array,
      default: () => [
        'از', 'با', 'برای', 'در', 'به', 'بر', 'روی', 'زیر', 'کنار',
        'جلوی', 'پشت', 'داخل', 'خارج', 'بالای', 'پایین', 'نزدیک',
        'دور', 'بین', 'میان', 'طریق', 'وسیلۀ', 'توسط', 'بجز', 'غیر',
        'مثل', 'مانند', 'همانند', 'چون', 'چرا', 'زیرا', 'چراکه',
        'که', 'تا', 'مگر', 'آیا', 'چه', 'چگونه', 'چطور', 'کجا',
        'کی', 'چند', 'چقدر', 'کدام', 'کدامین', 'این', 'آن', 'اینها',
        'آنها', 'ما', 'شما', 'او', 'ایشان', 'خود', 'خودی', 'یک', 'دو',
        'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده', 'صد', 'هزار',
        'و', 'یا', 'اما', 'پس', 'پس‌از', 'پیش', 'پیش‌از', 'بعد', 'بعداز'
      ]
    }
  },
  data() {
    return {
      searchQuery: '',
      expandedNodes: {}
    };
  },
  computed: {
    treeData() {
      if (!this.items || this.items.length === 0) return [];

      const pageMap = this.buildPageMap();
      const tree = [];
      const processed = new Set();
      const hasParent = new Set();

      // پیدا کردن صفحاتی که والد دارند (زیرمجموعه هستند)
      Object.values(pageMap).forEach(page => {
        page.outgoingLinks.forEach(link => {
          const target = Object.values(pageMap).find(p => p.url === link.url);
          if (target) {
            hasParent.add(target.url);
          }
        });
      });

      // مرتب‌سازی صفحات بر اساس تعداد کلمات کلیدی (کمترین اول)
      const sortedPages = Object.values(pageMap).sort((a, b) => a.keywordCount - b.keywordCount);

      sortedPages.forEach(page => {
        if (processed.has(page.url)) return;

        // بررسی اینکه صفحه زیرمجموعه دارد یا نه
        const hasChildren = this.hasChildren(page, pageMap);

        // اگر صفحه والد دارد (زیرمجموعه است) و خودش زیرمجموعه ندارد → نادیده بگیر
        if (hasParent.has(page.url) && !hasChildren) {
          return;
        }

        const node = this.buildTree(page, pageMap, processed, hasParent);
        if (node) {
          tree.push(node);
        }
      });

      return tree;
    },

    filteredTree() {
      let filtered = this.treeData.filter(x=>x.children.length>0 || x.isOrphan);

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = this.filterTreeBySearch(filtered, query);
      }

      return filtered;
    },

    totalPages() {
      return this.treeData.length;
    },

    pagesWithChildren() {
      return this.treeData.filter(node => node.children && node.children.length > 0).length;
    },

    orphanPages() {
      return this.treeData.filter(node => node.isOrphan).length;
    },
    Childrens() {
       const parents = this.treeData.filter(node => node.children && node.children.length > 0);
       let total = 0;
       parents.forEach(function (val){
         total+=val.children.length
       })
      return total
    }
  },
  methods: {
    buildPageMap() {
      const map = {};

      this.items.forEach(item => {
        const keywordCount = this.countKeywords(item.keyword || '');
        map[item.url] = {
          title: item.title,
          url: item.url,
          keyword: item.keyword,
          keywordCount: keywordCount,
          htmlContent: item.htmlContent,
          outgoingLinks: this.extractOutgoingLinks(item.htmlContent),
          incomingLinks: 0,
          anchorTexts: {}
        };
      });

      Object.values(map).forEach(page => {
        page.outgoingLinks.forEach(link => {
          const target = Object.values(map).find(p => p.url === link.url);
          if (target) {
            target.incomingLinks = (target.incomingLinks || 0) + 1;
            if (!target.anchorTexts[page.url]) {
              target.anchorTexts[page.url] = link.anchorText;
            }
          }
        });
      });

      return map;
    },

    extractOutgoingLinks(htmlContent) {
      if (!htmlContent) return [];

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const links = [];

      doc.querySelectorAll('a').forEach(link => {
        const href = link.getAttribute('href');
        const anchorText = link.textContent.trim();
        if (href && href.includes('/')) {
          links.push({
            url: href,
            anchorText: anchorText
          });
        }
      });

      return links;
    },

    hasChildren(page, pageMap) {
      return Object.values(pageMap).some(p => {
        if (p.url === page.url) return false;
        return p.outgoingLinks.some(link => link.url === page.url || decodeURI(link.url) ===  page.url);
      });
    },

    buildTree(page, pageMap, processed, hasParent) {
      if (processed.has(page.url)) return null;

      processed.add(page.url);

      const children = [];
      Object.values(pageMap).forEach(p => {
        if (p.url === page.url) return;
        if (processed.has(p.url)) return;

        const linkInfo = p.outgoingLinks.find(link => link.url === page.url);
        const hasLink = !!linkInfo;

        if (hasLink) {
          let isRelated = this.checkThematicRelation(p.keyword, page.keyword);
          isRelated=true
          if (isRelated) {
            const childNode = {
              title: p.title,
              url: p.url,
              keyword: p.keyword,
              keywordCount: p.keywordCount,
              anchorText: linkInfo ? linkInfo.anchorText : 'نامشخص',
              incomingLinks: p.incomingLinks || 0,
              outgoingLinks: p.outgoingLinks ? p.outgoingLinks.length : 0,
              parentUrl: page.url
            };
            children.push(childNode);
          }
        }
      });

      children.sort((a, b) => b.keywordCount - a.keywordCount);
// console.log("check is orphan",hasParent,page.url,children.length)
      // تشخیص یتیم: نه والد دارد و نه زیرمجموعه
      // const isOrphan = !hasParent.has(page.url) && children.length === 0;
      const isOrphan = page.outgoingLinks.length==0 && children.length === 0;

      return {
        title: page.title,
        url: page.url,
        keyword: page.keyword,
        keywordCount: page.keywordCount,
        incomingLinks: page.incomingLinks || 0,
        outgoingLinks: page.outgoingLinks ? page.outgoingLinks.length : 0,
        children: children,
        isOrphan: isOrphan,
        hasParent: hasParent.has(page.url),
        isExpanded: this.expandedNodes[page.url] !== undefined ? this.expandedNodes[page.url] : true
      };
    },

    checkThematicRelation(keyword1, keyword2) {
      if (!keyword1 || !keyword2) return false;

      const clean1 = this.removeStopWords(keyword1).join(' ');
      const clean2 = this.removeStopWords(keyword2).join(' ');

      if (!clean1 || !clean2) return false;

      const words1 = clean1.split(' ');
      const words2 = clean2.split(' ');

      return words2.every(word => words1.includes(word));
    },

    removeStopWords(text) {
      if (!text) return [];

      const words = text.split(/\s+/).filter(w => w.length > 0);

      return words.filter(word => {
        const cleanWord = word.replace(/[^آ-یa-zA-Z]/g, '').toLowerCase();
        return !this.stopWords.includes(cleanWord) && cleanWord.length > 0;
      });
    },

    countKeywords(text) {
      if (!text) return 0;
      const filtered = this.removeStopWords(text);
      return filtered.length;
    },

    filterTreeBySearch(tree, query) {
      const result = [];
      tree.forEach(node => {
        const nodeMatch = node.title.toLowerCase().includes(query) ||
            node.keyword.toLowerCase().includes(query);

        let childMatch = false;
        let filteredChildren = [];

        if (node.children) {
          filteredChildren = node.children.filter(child =>
              child.title.toLowerCase().includes(query) ||
              child.keyword.toLowerCase().includes(query)
          );
          childMatch = filteredChildren.length > 0;
        }

        if (nodeMatch || childMatch) {
          const newNode = {
            ...node,
            children: nodeMatch ? node.children : filteredChildren
          };
          result.push(newNode);
        }
      });
      return result;
    },

    toggleNode(node) {
      this.$set(this.expandedNodes, node.url, !node.isExpanded);
      node.isExpanded = !node.isExpanded;
    },

    expandAll() {
      this.treeData.forEach(node => {
        this.$set(this.expandedNodes, node.url, true);
        node.isExpanded = true;
      });
    },

    collapseAll() {
      this.treeData.forEach(node => {
        this.$set(this.expandedNodes, node.url, false);
        node.isExpanded = false;
      });
    }
  }
};
</script>

<style scoped>
.topic-clustering-tree {
  direction: rtl;
  text-align: right;
}

.text-value-lg {
  font-size: 2rem;
  font-weight: bold;
}

.tree-container {
  padding: 10px 0;
  max-height: 600px;
  overflow-y: auto;
}

.tree-node {
  margin-bottom: 4px;
}

.tree-node-item {
  padding: 10px 15px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.tree-node-item:hover {
  background-color: #f8f9fa;
}

.tree-node-pillar {
  background-color: #e3f2fd;
  border-right: 4px solid #0d6efd;
  font-weight: 500;
}

.tree-node-pillar:hover {
  background-color: #bbdefb;
}

.tree-node-orphan {
  background-color: #f8d7da;
  border-right: 4px solid #dc3545;
  font-weight: 500;
}

.tree-node-orphan:hover {
  background-color: #f5c6cb;
}

.tree-node-cluster {
  background-color: #f5f5f5;
  border-right: 3px solid #17a2b8;
  margin-right: 20px;
}

.tree-node-cluster:hover {
  background-color: #e9ecef;
}

.tree-toggle {
  cursor: pointer;
  display: inline-block;
  width: 24px;
  height: 24px;
  text-align: center;
  line-height: 24px;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.tree-toggle:hover {
  background-color: rgba(13, 110, 253, 0.1);
}

.tree-toggle-placeholder {
  display: inline-block;
  width: 24px;
  flex-shrink: 0;
}

.tree-title {
  font-size: 0.95rem;
}

.tree-children {
  margin-top: 2px;
}

.ml-2 {
  margin-right: 0.5rem !important;
}

.mr-1 {
  margin-left: 0.25rem !important;
}

.mr-2 {
  margin-left: 0.5rem !important;
}

.text-left {
  text-align: left !important;
}
</style>